// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DatePicker } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { GridContainerProps } from "@werner/MobiusOverrides/GridContainer/GridContainer";
import { ContactFormState } from "@werner/Overrides/Widgets/RequestRma/RequestRma.form.module";
import { xMask } from "@werner/XDL/Utilities";
import { CreateForm } from "@werner/XDL/Utilities/form";
import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    contactForm?: CreateForm<ContactFormState>;
}

const mapStateToProps = (state: ApplicationState) => ({
    returnNotes: state.pages.requestRma.returnNotes,
});

interface ContactFormStyles {
    gridContainer?: GridContainerProps;
    contactNameGridItem?: GridItemProps;
    phoneNumberGridItem?: GridItemProps;
    address1GridItem?: GridItemProps;
    address2GridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    pickupDateGridItem?: GridItemProps;
    textField?: TextFieldPresentationProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    contactFormTitle?: TypographyProps;
    contactFormTitleGridItem?: GridItemProps;
    pickupDateField?: DatePickerPresentationProps;
    returnNotesGridItem?: GridItemProps;
}

const contactFormStyles: ContactFormStyles = {
    contactNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    phoneNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    address1GridItem: {
        width: [12, 12, 6, 6, 6],
    },
    address2GridItem: {
        width: [12, 12, 6, 6, 6],
    },
    cityGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    stateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    zipCodeGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    pickupDateGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            div {
                width: 100%;
            }
        `,
    },
    returnNotesGridItem: {
        width: 12,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = contactFormStyles;
const RequestRmaContactForm: FC<Props> = ({ contactForm, returnNotes }) => {
    if (!contactForm) {
        return null;
    }

    const { fields, isSubmitted } = contactForm.state;

    useEffect(() => {
        contactForm?.onFieldChange(fields.returnNotes, returnNotes);
    }, [returnNotes]);

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.contactNameGridItem}>
                <TextField
                    value={fields.name.value}
                    onChange={e => contactForm?.onFieldChange(fields.name, e.currentTarget.value)}
                    error={isSubmitted && fields.name.error}
                    {...fields.name.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.phoneNumberGridItem}>
                <TextField
                    value={xMask.phoneNumber(fields.phone.value || "")}
                    onChange={e => contactForm?.onFieldChange(fields.phone, e.currentTarget.value)}
                    error={isSubmitted && fields.phone.error}
                    {...fields.phone.attrs}
                    {...styles.phoneNumberTextField}
                />
            </GridItem>
            <GridItem {...styles.address1GridItem}>
                <TextField
                    value={fields.address1.value}
                    onChange={e => contactForm?.onFieldChange(fields.address1, e.currentTarget.value)}
                    error={isSubmitted && fields.address1.error}
                    {...fields.address1.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.address2GridItem}>
                <TextField
                    value={fields.address2.value}
                    onChange={e => contactForm?.onFieldChange(fields.address2, e.currentTarget.value)}
                    error={isSubmitted && fields.address2.error}
                    {...fields.address2.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.cityGridItem}>
                <TextField
                    value={fields.city.value}
                    onChange={e => contactForm?.onFieldChange(fields.city, e.currentTarget.value)}
                    error={isSubmitted && fields.city.error}
                    {...fields.city.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.stateGridItem}>
                <TextField
                    value={fields.state.value}
                    onChange={e => contactForm?.onFieldChange(fields.state, e.currentTarget.value)}
                    error={isSubmitted && fields.state.error}
                    {...fields.state.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.zipCodeGridItem}>
                <TextField
                    value={fields.zipCode.value}
                    onChange={e => contactForm?.onFieldChange(fields.zipCode, e.currentTarget.value)}
                    error={isSubmitted && fields.zipCode.error}
                    {...fields.zipCode.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.pickupDateGridItem}>
                <DatePicker
                    label={fields.pickupDate.attrs?.label}
                    {...styles.pickupDateField}
                    dateTimePickerProps={{
                        minDate: new Date(),
                        ...styles.pickupDateField?.dateTimePickerProps,
                    }}
                    onDayChange={(pickerState: DatePickerState) =>
                        contactForm?.onFieldChange(fields.pickupDate, pickerState.selectedDay)
                    }
                    error={isSubmitted && fields.pickupDate.error}
                    {...(fields.pickupDate.value ? { selectedDay: new Date(fields.pickupDate.value) } : undefined)}
                />
            </GridItem>
            <GridItem {...styles.returnNotesGridItem}>
                {createWidgetElement("RequestRma/RequestRmaNotes", {
                    fields: {},
                    isSubmitted,
                    errorText: fields.returnNotes.error,
                } as any)}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(RequestRmaContactForm);
